// English messages for the source part of the interface. Spread into en.ts.
// Same rules as en.ts: strings that existed before translation are kept word
// for word, so the screen tests (which run in English) describe the same screen.

import type { Message, Vars } from './en.ts';

const n = (vars: Vars, name = 'count'): number => Number(vars[name] ?? 0);

export const sourceEn = {
  // --- SourceViewer: opening the source ---------------------------------------
  'source.opening': 'Opening the stored source…',
  'source.tryAgain': 'Try again',
  'source.noVersion': 'No source version is stored for this document.',
  'source.couldNotOpen': 'The stored source could not be opened.',
  'source.noStored': 'No source document is stored for this version.',
  'source.noStoredDetail':
    'This document was recorded without its bytes, so there is nothing to show. Its metadata and history are below.',
  'source.unreachable': 'The service could not be reached.',
  'source.unreachableDetail': 'Nothing is known about the source right now, which is not the same as nothing being there.',
  'source.absent': 'The source document could not be opened.',
  'source.absentDetail': 'Either it is no longer available to your session, or this deployment does not issue viewer URLs.',

  // --- SourceViewer: the page ------------------------------------------------
  'source.openFullSize': 'Open the source full size',
  'source.zoomHint': 'Click to open full size',
  'source.fullSize': 'The source, full size',
  'source.close': 'Close',
  'source.imageAlt': 'The stored source document',
  'source.preparing': 'Preparing the source…',
  'source.unrenderable': (v: Vars) =>
    `This source is ${v['mediaType'] === '' ? 'of an unreported type' : String(v['mediaType'])}, which this screen cannot display. It is stored and unchanged; nothing here is a substitute for looking at it.`,

  // --- SourceViewer: the caption under the page -------------------------------
  'source.legend.version': 'Version {version} · {mediaType} · {bytes} bytes',
  'source.legend.pages': (v: Vars) => `${n(v)} ${n(v) === 1 ? 'page' : 'pages'}`,
  'source.legend.scanning': 'Your AI is reading this page now; what it finds appears beside it.',
  'source.legend.notExtracted': 'Nothing has been extracted from this document yet, so nothing is highlighted.',
  'source.legend.highlighted': 'Highlighted regions are the evidence the extraction recorded for each field.',
  'source.legend.otherVersion':
    'The extracted fields below were read from a different source version, so nothing is highlighted here.',
  'source.legend.ocrBoxes':
    'Highlighted regions are where OCR read each field’s text, exactly as quoted. A field whose text was not found exactly once is listed without a location.',
  'source.legend.pdfLocated':
    'Highlighted text is the evidence the extraction recorded, found on the rendered page. A field whose evidence was not found exactly once is listed without a location.',
  'source.legend.pdfNoExcerpt': 'No field carries a recorded evidence excerpt, so nothing on the page is highlighted.',
  'source.legend.pdfNoneFound':
    'None of the recorded evidence was found exactly once on the rendered page, so nothing is highlighted.',
  'source.legend.imageNoPlace':
    'No field of this {mediaType} source has a recorded place on the image, so the fields are shown without a location rather than with a guessed one.',

  // --- SourceViewer: the field list -------------------------------------------
  'source.heading.page': 'What your AI read from this page',
  'source.heading.document': 'What your AI read from this document',
  'source.reading': 'Reading…',
  'source.nothingRead': 'Nothing has been read from it yet.',
  'source.noFields': 'This reading recorded no fields.',
  'source.moreFields': (v: Vars) => `${n(v)} more ${n(v) === 1 ? 'field' : 'fields'} read`,
  'source.confidence.none': 'Confidence not measured for any field.',
  'source.confidence.all': 'Confidence is shown on each field, as measured.',
  'source.confidence.some': 'Confidence not measured for {unmeasured} of {total} fields; the others show it as measured.',
  'source.confidence.value': 'confidence {percent}',
  'source.derived': '{how} — the model did not report it',
  'source.derived.amount_markers': 'derived from the amount markers',
  'source.derived.vat_breakdown': 'derived from the VAT breakdown',
  'source.derived.arithmetic': 'derived: the lines and VAT rows on the page add up to it',
  'source.derived.person': 'confirmed by a person: corrected in the letter position, matching an existing relation',
  'source.noPlace': 'No place on the page recorded',
  'source.found': 'found',
  'source.foundOnPage': 'found on page {page}',
  'source.spanNotShown': 'Span recorded, not shown here',
  'source.pageNotShown': 'Page {page}, not shown here',
  'source.pdf.noText': 'Span recorded without its text, not shown here',
  'source.pdf.looking': 'Looking on the page…',
  'source.pdf.ambiguous': 'Appears more than once on the page, so not marked',
  'source.pdf.notFound': 'Not found on the rendered page, so not marked',

  // --- Field labels (by canonical field name) ---------------------------------
  'source.field.documentType': 'Document type',
  'source.field.supplierName': 'Supplier',
  'source.field.supplierVatNumber': 'VAT number',
  'source.field.supplierRegistrationNumber': 'KvK number',
  'source.field.invoiceNumber': 'Invoice number',
  'source.field.invoiceDate': 'Invoice date',
  'source.field.dueDate': 'Due date',
  'source.field.currency': 'Currency',
  'source.field.paymentMethod': 'Paid with',
  'source.field.originalInvoiceNumber': 'Corrects invoice',
  'source.field.totalNet': 'Net',
  'source.field.totalTax': 'VAT',
  'source.field.total': 'Total',
  'source.field.line': 'Line {n} {part}',
  'source.field.vatRow': 'VAT row {n} {part}',
  'source.line.description': 'description',
  'source.line.quantity': 'quantity',
  'source.line.unitPrice': 'unit price',
  'source.line.net': 'net',
  'source.line.taxRate': 'VAT rate',

  // --- PdfView -----------------------------------------------------------------
  'source.pdf.rendering': 'Rendering the stored PDF…',
  'source.pdf.failed':
    'This PDF could not be rendered here. It is stored and unchanged; nothing on this screen is a substitute for looking at it.',
  'source.pdf.pageLimit': 'The first {shown} of {pages} pages are shown.',

  // --- SupplierSuggestion ------------------------------------------------------
  'source.suggest.matchedOn.vat_number': 'VAT number',
  'source.suggest.matchedOn.registration_number': 'KvK number',
  'source.suggest.noneTitle': 'No matching relation in the accounting package',
  'source.suggest.noneBody':
    'No relation there has this supplier\'s VAT or KvK number. Create the relation in the accounting package, then press “Check now” on Connections; the suggestion appears here once it has been read.',
  'source.suggest.ambiguousTitle': '{count} relations share this {matchedOn}',
  'source.suggest.ambiguousBody':
    'Which one this supplier is cannot be told from the number, so nothing is suggested. A person has to decide it in the booking rules.',
  'source.suggest.title': 'Suggested supplier rule',
  'source.suggest.badge': 'Suggestion · not applied',
  'source.suggest.relationBefore': 'This supplier is relation ',
  'source.suggest.relationAfter': ' in the accounting package: the same {matchedOn} ({identifier}).',
  'source.suggest.costLedger': 'Cost ledger',
  'source.suggest.chooseLedger': 'Choose a ledger…',
  'source.suggest.usedBefore': 'Used for this relation before',
  'source.suggest.ledgerUse': '{ledger} — {invoices} of {read} past invoices',
  'source.suggest.chart': 'The chart',
  'source.suggest.noHistory':
    'No past purchase invoice of this relation has been read from the accounting package, so no ledger is suggested.',
  'source.suggest.noneInChart': (v: Vars) =>
    `None of the ${n(v, 'read')} past ${n(v, 'read') === 1 ? 'invoice' : 'invoices'} of this relation used a ledger in the current chart, so no ledger is suggested.`,
  'source.suggest.notUsed': (v: Vars) =>
    `This ledger was not used on the ${n(v, 'read')} past ${n(v, 'read') === 1 ? 'invoice' : 'invoices'} of this relation that were read.`,
  'source.suggest.usedOn': (v: Vars) =>
    `Used on ${n(v, 'invoices')} of the ${n(v, 'read')} past ${n(v, 'read') === 1 ? 'invoice' : 'invoices'} of this relation that were read.`,
  'source.suggest.nonDeductible':
    'VAT on this supplier\'s invoices is not deductible (e.g. horeca): book the gross amount and reclaim no VAT',
  'source.suggest.use': 'Use this rule',
  'source.suggest.useNote': 'Saves the rule for this {matchedOn} and reads the document again. Nothing is booked.',
} satisfies Record<string, Message>;
